import { jsPDF } from "jspdf";
import type { EOIFormData } from "../EOIForm";

// A4 dimensions in mm
const PAGE_W = 210;
const MARGIN_X = 15;
const MARGIN_TOP = 10;
const USABLE_W = PAGE_W - 2 * MARGIN_X; // 180mm

// Column helpers
const col2 = USABLE_W / 2; // ~90mm

// Colors
const HEADING_BG = [18, 28, 71]; // brand #121c47
const DIVIDER_COLOR = [200, 200, 200];
const FIELD_BG = [245, 245, 245];
const FIELD_BORDER = [180, 180, 180];

// Field sizing — compact to fit single A4 page
const FIELD_H = 5;
const LABEL_FONT_SIZE = 7;
const FIELD_FONT_SIZE = 8;
const LABEL_OFFSET = 3; // mm above field reserved for label text
const ROW_ADVANCE = LABEL_OFFSET + FIELD_H + 2; // 10mm per labeled-field row

let cursorY = MARGIN_TOP;

function resetCursor() {
  cursorY = MARGIN_TOP;
}

function advanceCursor(amount: number) {
  cursorY += amount;
}

function drawSectionHeading(doc: jsPDF, text: string) {
  doc.setFillColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.rect(MARGIN_X, cursorY, USABLE_W, 5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(text, MARGIN_X + 2, cursorY + 3.5);
  doc.setTextColor(0, 0, 0);
  advanceCursor(6);
}

function drawDivider(doc: jsPDF) {
  doc.setDrawColor(DIVIDER_COLOR[0], DIVIDER_COLOR[1], DIVIDER_COLOR[2]);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_X, cursorY, MARGIN_X + USABLE_W, cursorY);
  advanceCursor(2);
}

function addLabeledTextField(
  doc: jsPDF,
  label: string,
  fieldName: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number = FIELD_H
) {
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(60, 60, 60);
  doc.text(label, x, y - 1);
  doc.setTextColor(0, 0, 0);

  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tf = new (doc as any).AcroFormTextField();
  tf.fieldName = fieldName;
  tf.x = x;
  tf.y = y;
  tf.width = width;
  tf.height = height;
  tf.fontSize = FIELD_FONT_SIZE;
  tf.fontName = "Helvetica";
  tf.defaultValue = value;
  tf.value = value;
  doc.addField(tf);
}

function addMultilineTextField(
  doc: jsPDF,
  fieldName: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number
) {
  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tf = new (doc as any).AcroFormTextField();
  tf.fieldName = fieldName;
  tf.x = x;
  tf.y = y;
  tf.width = width;
  tf.height = height;
  tf.fontSize = FIELD_FONT_SIZE;
  tf.fontName = "Helvetica";
  tf.multiline = true;
  tf.defaultValue = value;
  tf.value = value;
  doc.addField(tf);
}

function addCheckboxField(
  doc: jsPDF,
  label: string,
  fieldName: string,
  checked: boolean,
  x: number,
  y: number
) {
  const cbSize = 3.5;

  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, cbSize, cbSize, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cb = new (doc as any).AcroFormCheckBox();
  cb.fieldName = fieldName;
  cb.x = x;
  cb.y = y;
  cb.width = cbSize;
  cb.height = cbSize;
  if (checked) {
    cb.appearanceState = "On";
    cb.value = "On";
    cb.defaultValue = "On";
  } else {
    cb.appearanceState = "Off";
    cb.value = "Off";
    cb.defaultValue = "Off";
  }
  doc.addField(cb);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "normal");
  doc.text(label, x + cbSize + 1.5, y + 2.8);
}

function addInlineTextField(
  doc: jsPDF,
  fieldName: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number = FIELD_H
) {
  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tf = new (doc as any).AcroFormTextField();
  tf.fieldName = fieldName;
  tf.x = x;
  tf.y = y;
  tf.width = width;
  tf.height = height;
  tf.fontSize = FIELD_FONT_SIZE;
  tf.fontName = "Helvetica";
  tf.defaultValue = value;
  tf.value = value;
  doc.addField(tf);
}

async function loadLogoAsBase64(): Promise<string | null> {
  try {
    const response = await fetch("/assets/One-logo.png");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateEOIPDF(formData: EOIFormData): Promise<void> {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  resetCursor();

  const logoBase64 = await loadLogoAsBase64();

  const gap = 3;
  const fieldW3 = (USABLE_W - 2 * gap) / 3;
  const fieldW2 = (USABLE_W - gap) / 2;
  const fieldW4 = (USABLE_W - 3 * gap) / 4;
  const lo = LABEL_OFFSET; // shorthand for field Y offset from cursorY

  // ==================== HEADER (18mm) ====================
  if (logoBase64) {
    // Original image is 294x330px; maintain aspect ratio with 15mm width
    const logoW = 15;
    const logoH = logoW * (330 / 294); // ~16.8mm
    doc.addImage(logoBase64, "PNG", MARGIN_X, cursorY, logoW, logoH);
  }

  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text("EOI / SALES INFORMATION SHEET", PAGE_W / 2, cursorY + 8, {
    align: "center",
  });

  doc.setFontSize(5.5);
  doc.setFont("helvetica", "bold");
  const rightX = MARGIN_X + USABLE_W;
  doc.text("One Property Australia", rightX, cursorY + 2.5, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.text("ACN: 664 898 846", rightX, cursorY + 5, { align: "right" });
  doc.text("6/22 Alexandra Pl,", rightX, cursorY + 7.5, { align: "right" });
  doc.text("Murarrie QLD 4172", rightX, cursorY + 10, { align: "right" });
  doc.text("ONEPROPERTYAUSTRALIA.COM.AU", rightX, cursorY + 12.5, {
    align: "right",
  });

  advanceCursor(18);

  // ==================== TOP ROW: Date / Sales Channel / BDM (10mm) ====================
  addLabeledTextField(doc, "Date:", "date", formData.date, MARGIN_X, cursorY + lo, fieldW3);
  addLabeledTextField(doc, "Sales Channel:", "salesChannel", formData.salesChannel, MARGIN_X + fieldW3 + gap, cursorY + lo, fieldW3);
  addLabeledTextField(doc, "BDM:", "bdm", formData.bdm, MARGIN_X + 2 * (fieldW3 + gap), cursorY + lo, fieldW3);
  advanceCursor(ROW_ADVANCE);
  drawDivider(doc);

  // ==================== CLIENT DETAILS ====================
  drawSectionHeading(doc, "CLIENT DETAILS");

  // Client A (3 cols)
  addLabeledTextField(doc, "First Name (Client A):", "firstNameClientA", formData.firstNameClientA, MARGIN_X, cursorY + lo, fieldW3);
  addLabeledTextField(doc, "Middle Name (Client A):", "middleNameClientA", formData.middleNameClientA, MARGIN_X + fieldW3 + gap, cursorY + lo, fieldW3);
  addLabeledTextField(doc, "Last Name (Client A):", "lastNameClientA", formData.lastNameClientA, MARGIN_X + 2 * (fieldW3 + gap), cursorY + lo, fieldW3);
  advanceCursor(ROW_ADVANCE);

  // Client B (3 cols)
  addLabeledTextField(doc, "First Name (Client B):", "firstNameClientB", formData.firstNameClientB, MARGIN_X, cursorY + lo, fieldW3);
  addLabeledTextField(doc, "Middle Name (Client B):", "middleNameClientB", formData.middleNameClientB, MARGIN_X + fieldW3 + gap, cursorY + lo, fieldW3);
  addLabeledTextField(doc, "Last Name (Client B):", "lastNameClientB", formData.lastNameClientB, MARGIN_X + 2 * (fieldW3 + gap), cursorY + lo, fieldW3);
  advanceCursor(ROW_ADVANCE);

  // Address / Phone Primary / Phone Secondary (2fr 1fr 1fr)
  const w2fr = (USABLE_W - 2 * gap) / 2;
  const w1fr = (USABLE_W - 2 * gap) / 4;
  addLabeledTextField(doc, "Address:", "address", formData.address, MARGIN_X, cursorY + lo, w2fr);
  addLabeledTextField(doc, "Phone (Primary):", "phonePrimary", formData.phonePrimary, MARGIN_X + w2fr + gap, cursorY + lo, w1fr);
  addLabeledTextField(doc, "Phone (Secondary):", "phoneSecondary", formData.phoneSecondary, MARGIN_X + w2fr + w1fr + 2 * gap, cursorY + lo, w1fr);
  advanceCursor(ROW_ADVANCE);

  // Emails (2 cols)
  addLabeledTextField(doc, "Email (Primary):", "emailPrimary", formData.emailPrimary, MARGIN_X, cursorY + lo, fieldW2);
  addLabeledTextField(doc, "Email (Secondary):", "emailSecondary", formData.emailSecondary, MARGIN_X + fieldW2 + gap, cursorY + lo, fieldW2);
  advanceCursor(ROW_ADVANCE);

  // Purchasing Entity (full width)
  addLabeledTextField(doc, "Purchasing Entity:", "purchasingEntity", formData.purchasingEntity, MARGIN_X, cursorY + lo, USABLE_W);
  advanceCursor(ROW_ADVANCE);

  // Customer Type checkboxes
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Customer Type:", MARGIN_X, cursorY + 3);
  const cbStartX = MARGIN_X + 28;
  const cbSpacing = 30;
  addCheckboxField(doc, "Buyer", "customerTypeBuyer", formData.customerTypeBuyer, cbStartX, cursorY);
  addCheckboxField(doc, "Owner Occupier", "customerTypeOwnerOccupier", formData.customerTypeOwnerOccupier, cbStartX + cbSpacing, cursorY);
  addCheckboxField(doc, "Investor", "customerTypeInvestor", formData.customerTypeInvestor, cbStartX + 2 * cbSpacing, cursorY);
  addCheckboxField(doc, "SMSF", "customerTypeSMSF", formData.customerTypeSMSF, cbStartX + 3 * cbSpacing, cursorY);
  advanceCursor(6);

  drawDivider(doc);

  // ==================== PROPERTY DETAILS ====================
  drawSectionHeading(doc, "PROPERTY DETAILS");

  // Estate / Stage / Lot / Street (4 cols)
  addLabeledTextField(doc, "Estate Name:", "estateName", formData.estateName, MARGIN_X, cursorY + lo, fieldW4);
  addLabeledTextField(doc, "Stage:", "stage", formData.stage, MARGIN_X + fieldW4 + gap, cursorY + lo, fieldW4);
  addLabeledTextField(doc, "Lot Number:", "lotNumber", formData.lotNumber, MARGIN_X + 2 * (fieldW4 + gap), cursorY + lo, fieldW4);
  addLabeledTextField(doc, "Street:", "street", formData.street, MARGIN_X + 3 * (fieldW4 + gap), cursorY + lo, fieldW4);
  advanceCursor(ROW_ADVANCE);

  // House Design / Facade / Land Size (1fr 1fr 2fr)
  const w1frProp = (USABLE_W - 2 * gap) / 4;
  const w2frProp = w1frProp * 2;
  addLabeledTextField(doc, "House Design:", "houseDesign", formData.houseDesign, MARGIN_X, cursorY + lo, w1frProp);
  addLabeledTextField(doc, "Facade:", "facade", formData.facade, MARGIN_X + w1frProp + gap, cursorY + lo, w1frProp);
  addLabeledTextField(doc, "Land Size:", "landSize", formData.landSize, MARGIN_X + 2 * (w1frProp + gap), cursorY + lo, w2frProp);
  advanceCursor(ROW_ADVANCE);

  // Build $ / Land $ / Total $ (3 cols)
  addLabeledTextField(doc, "Build $:", "buildPrice", formData.buildPrice, MARGIN_X, cursorY + lo, fieldW3);
  addLabeledTextField(doc, "Land $:", "landPrice", formData.landPrice, MARGIN_X + fieldW3 + gap, cursorY + lo, fieldW3);
  addLabeledTextField(doc, "Total $:", "totalPrice", formData.totalPrice, MARGIN_X + 2 * (fieldW3 + gap), cursorY + lo, fieldW3);
  advanceCursor(ROW_ADVANCE);

  // Land Registered / Property Purchased checkboxes
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Land Registered:", MARGIN_X, cursorY + 3);
  addCheckboxField(doc, "Yes", "landRegisteredYes", formData.landRegisteredYes, MARGIN_X + 30, cursorY);
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "normal");
  doc.text("Expected:", MARGIN_X + 45, cursorY + 3);
  addInlineTextField(doc, "landRegisteredExpectedDate", formData.landRegisteredExpectedDate, MARGIN_X + 60, cursorY, 25);

  doc.text("Property Purchased:", MARGIN_X + col2, cursorY + 3);
  addCheckboxField(doc, "Yes", "propertyPurchasedYes", formData.propertyPurchasedYes, MARGIN_X + col2 + 35, cursorY);
  addCheckboxField(doc, "EOI", "propertyPurchasedEOI", formData.propertyPurchasedEOI, MARGIN_X + col2 + 50, cursorY);
  advanceCursor(6);

  drawDivider(doc);

  // ==================== SOLICITOR & FINANCE ====================
  drawSectionHeading(doc, "SOLICITOR & FINANCE");

  // Solicitor Company / Contact (2 cols)
  addLabeledTextField(doc, "Solicitor Company:", "solicitorCompany", formData.solicitorCompany, MARGIN_X, cursorY + lo, fieldW2);
  addLabeledTextField(doc, "Contact:", "solicitorContact", formData.solicitorContact, MARGIN_X + fieldW2 + gap, cursorY + lo, fieldW2);
  advanceCursor(ROW_ADVANCE);

  // Solicitor Phone / Email (2 cols)
  addLabeledTextField(doc, "Phone:", "solicitorPhone", formData.solicitorPhone, MARGIN_X, cursorY + lo, fieldW2);
  addLabeledTextField(doc, "Email:", "solicitorEmail", formData.solicitorEmail, MARGIN_X + fieldW2 + gap, cursorY + lo, fieldW2);
  advanceCursor(ROW_ADVANCE);

  // Solicitor Address (full width)
  addLabeledTextField(doc, "Address:", "solicitorAddress", formData.solicitorAddress, MARGIN_X, cursorY + lo, USABLE_W);
  advanceCursor(ROW_ADVANCE);

  // Finance Company / Contact (2 cols)
  addLabeledTextField(doc, "Finance Company:", "financeCompany", formData.financeCompany, MARGIN_X, cursorY + lo, fieldW2);
  addLabeledTextField(doc, "Contact:", "financeContact", formData.financeContact, MARGIN_X + fieldW2 + gap, cursorY + lo, fieldW2);
  advanceCursor(ROW_ADVANCE);

  // Finance Phone / Email (2 cols)
  addLabeledTextField(doc, "Phone:", "financePhone", formData.financePhone, MARGIN_X, cursorY + lo, fieldW2);
  addLabeledTextField(doc, "Email:", "financeEmail", formData.financeEmail, MARGIN_X + fieldW2 + gap, cursorY + lo, fieldW2);
  advanceCursor(ROW_ADVANCE);

  drawDivider(doc);

  // ==================== CONTRACT DETAILS ====================
  drawSectionHeading(doc, "CONTRACT DETAILS");

  // Contracts immediately?
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Do you require contracts to be sent out immediately for signing?", MARGIN_X, cursorY + 3);
  addCheckboxField(doc, "Yes", "contractsImmediatelyYes", formData.contractsImmediatelyYes, MARGIN_X + 110, cursorY);
  addCheckboxField(doc, "No", "contractsImmediatelyNo", formData.contractsImmediatelyNo, MARGIN_X + 125, cursorY);
  advanceCursor(6);

  // Contracts sent to / Via
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Contracts to be sent to:", MARGIN_X, cursorY + 3);
  addCheckboxField(doc, "Buyer's Solicitor", "contractsSentToBuyerSolicitor", formData.contractsSentToBuyerSolicitor, MARGIN_X + 42, cursorY);
  addCheckboxField(doc, "Agent", "contractsSentToAgent", formData.contractsSentToAgent, MARGIN_X + 75, cursorY);

  doc.text("Via:", MARGIN_X + col2, cursorY + 3);
  addCheckboxField(doc, "DocuSign", "contractsViaDocuSign", formData.contractsViaDocuSign, MARGIN_X + col2 + 10, cursorY);
  addCheckboxField(doc, "Email", "contractsViaEmail", formData.contractsViaEmail, MARGIN_X + col2 + 35, cursorY);
  advanceCursor(6);

  // Ownership % Split
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Ownership % Split:", MARGIN_X, cursorY + 4);
  doc.setFont("helvetica", "normal");
  doc.text("(Client A)", MARGIN_X + 33, cursorY + 4);
  addInlineTextField(doc, "ownershipPercentClientA", formData.ownershipPercentClientA, MARGIN_X + 50, cursorY, 18);
  doc.text("% /", MARGIN_X + 70, cursorY + 4);
  addInlineTextField(doc, "ownershipPercentClientB", formData.ownershipPercentClientB, MARGIN_X + 78, cursorY, 18);
  doc.text("% (Client B)", MARGIN_X + 98, cursorY + 4);
  advanceCursor(8);

  // Special conditions
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Are there any special conditions required by the purchaser?", MARGIN_X, cursorY + 3);
  addCheckboxField(doc, "Yes", "specialConditionsYes", formData.specialConditionsYes, MARGIN_X + 105, cursorY);
  addCheckboxField(doc, "No", "specialConditionsNo", formData.specialConditionsNo, MARGIN_X + 120, cursorY);
  advanceCursor(5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.text("If yes, please provide details below:", MARGIN_X, cursorY + 2);
  advanceCursor(3);

  addMultilineTextField(doc, "specialConditionsDetails", formData.specialConditionsDetails, MARGIN_X, cursorY, USABLE_W, 12);
  advanceCursor(14);

  // Subject to finance
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Is this contract subject to finance?", MARGIN_X, cursorY + 3);
  addCheckboxField(doc, "Yes", "subjectToFinanceYes", formData.subjectToFinanceYes, MARGIN_X + 65, cursorY);
  addCheckboxField(doc, "No", "subjectToFinanceNo", formData.subjectToFinanceNo, MARGIN_X + 80, cursorY);
  advanceCursor(6);

  // Deposit due date
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("If not subject to finance - Please specify Deposit Due Date:", MARGIN_X, cursorY + 3);
  advanceCursor(5);
  addCheckboxField(doc, "14 days", "depositDueDate14Days", formData.depositDueDate14Days, MARGIN_X, cursorY);
  addCheckboxField(doc, "21 days", "depositDueDate21Days", formData.depositDueDate21Days, MARGIN_X + 25, cursorY);
  addCheckboxField(doc, "28 days", "depositDueDate28Days", formData.depositDueDate28Days, MARGIN_X + 50, cursorY);
  advanceCursor(5);

  doc.setFont("helvetica", "normal");
  doc.text("How many days?", MARGIN_X, cursorY + 4);
  addInlineTextField(doc, "depositDueDateCustomDays", formData.depositDueDateCustomDays, MARGIN_X + 28, cursorY, 25);
  advanceCursor(8);

  // ==================== FOOTER NOTE (10mm) ====================
  doc.setFillColor(245, 245, 245);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(MARGIN_X, cursorY, USABLE_W, 10, "FD");
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");
  doc.text("Please Note:", MARGIN_X + 2, cursorY + 3.5);
  doc.setFont("helvetica", "normal");
  doc.text(
    "The Solicitor Company mentioned above will be in touch to outline the EOI deposit amount and the Trust Account",
    MARGIN_X + 2,
    cursorY + 6.5
  );
  doc.text(
    "details for where you are to place the EOI deposit.",
    MARGIN_X + 2,
    cursorY + 9
  );

  // Save
  doc.save("EOI_Sales_Information_Sheet.pdf");
}
